import {FedopsInteractions} from '@wix/wixstores-client-storefront-sdk/dist/es/src/enums/fedopsInteractions';
import {mapUserInputSelectionMapToChoices} from '../../mappers/mapUserInputSelectionMapToChoices';
import {RootStore} from '../../stores/RootStore';
import {getAppErrorHandler} from './getAppErrorHandler';
import {getCtaButtonClickHandler} from './getCtaButtonClickHandler';
import {WidgetEventHandler} from './types';
import {AfterAddToCartChoices, CtaButtonsWidgetComponentIds} from '../../../ctaButtonsWidget/config/constants';
import {mapUserInputTextToFields} from '../../mappers/mapUserInputTextToFields';
import {action} from 'mobx';
import {SPECS} from '../../../../specs';
import {createCheckout} from '../createCheckout';
import {interruptAction} from './interruptAction';
import {TrackEventName} from '@wix/wixstores-client-core';
import {publishInitiateCheckoutTrackEvent} from '../product-service/publishInitiateCheckoutTrackEvent';

const publishAddToCartTrackEvent = (rootStore: RootStore) => {
  return rootStore.wixCodeApi.window.trackEvent(TrackEventName.ADD_TO_CART, {...rootStore.$state.trackParams} as any);
};

const getSelectedAction = (rootStore: RootStore, isPreOrder: boolean) => {
  const flowAPI = rootStore.controllerParams.flowAPI;
  const isMobile = flowAPI.environment.isMobile;
  const settings = rootStore.services.components.ctaButtonsWidget.settings;
  const afterAddToCartDesktop = settings?.afterAddToCart;
  const afterAddToCartMobile = settings?.afterAddToCartMobile ?? afterAddToCartDesktop;
  const addToCartAction = isMobile ? afterAddToCartMobile : afterAddToCartDesktop;
  const afterCompletionAction = isPreOrder ? settings?.afterPreOrder : addToCartAction;
  return afterCompletionAction || AfterAddToCartChoices.MINI_CART;
};

function preloadCartByAction(ecomPlatformPublicAPI, addToCartAction: AfterAddToCartChoices) {
  if (addToCartAction === AfterAddToCartChoices.TINY_CART) {
    return ecomPlatformPublicAPI.cart.preloadTinyCart();
  } else if (addToCartAction === AfterAddToCartChoices.MINI_CART) {
    return ecomPlatformPublicAPI.cart.preloadSideCart();
  } else if (addToCartAction === AfterAddToCartChoices.CART) {
    return ecomPlatformPublicAPI.cart.preloadCartPage();
  }
}

const onAddToCartCompleted = (rootStore: RootStore, isPreOrder) => async (): Promise<void> => {
  const flowAPI = rootStore.controllerParams.flowAPI;
  const isMS3 = flowAPI.experiments.enabled(SPECS.PRODUCT_PAGE_BLOCKS_MS3);
  const isMobile = flowAPI.environment.isMobile;
  const selectedAction = getSelectedAction(rootStore, isPreOrder);
  const buttonComponentId =
    isMS3 && isPreOrder ? CtaButtonsWidgetComponentIds.PreOrderButton : CtaButtonsWidgetComponentIds.AddToCartBtn;
  const shouldRemoveAddToCartAndCheckoutTrackEvents = flowAPI.experiments.enabled(
    SPECS.RemoveAddToCartAndCheckoutTrackEvents,
  );

  if (selectedAction === AfterAddToCartChoices.CHECKOUT) {
    return createCheckout(rootStore, {withNavigateToCheckout: true, isPreOrder})
      .then((checkout) => {
        if (!shouldRemoveAddToCartAndCheckoutTrackEvents) {
          publishInitiateCheckoutTrackEvent(rootStore, checkout.checkoutId as string);
        }
      })
      .then(() => Promise.resolve());
  }

  if (!shouldRemoveAddToCartAndCheckoutTrackEvents) {
    publishAddToCartTrackEvent(rootStore);
  }

  switch (selectedAction) {
    case AfterAddToCartChoices.CART:
      return rootStore.ecomPublicApi.navigate.toCart();

    case AfterAddToCartChoices.MINI_CART:
      try {
        return Promise.resolve(await rootStore.ecomPublicApi.cart.openSideCart());
      } catch {
        return rootStore.ecomPublicApi.navigate.toCart();
      }
    case AfterAddToCartChoices.TINY_CART:
      if (isMobile) {
        return rootStore.ecomPublicApi.navigate.toCart();
      }
      return Promise.resolve(rootStore.ecomPublicApi.cart.showTinycart());
    case AfterAddToCartChoices.NONE:
      rootStore.$state.isAddToCartInProgress = false;
      return rootStore.services.components.ctaButtonsWidget.indicateAddToCartCompleted(buttonComponentId);
    default:
      return Promise.resolve();
  }
};

const handleAddToCart = async (rootStore: RootStore): Promise<void> => {
  rootStore.$state.isAddToCartInProgress = true;
  const productId = rootStore.$state.product.id;
  const quantity = rootStore.$state.quantityInput;
  const preOrderRequested = rootStore.$state.isPreOrder;
  const customTextFields = mapUserInputTextToFields(rootStore);
  const choices = mapUserInputSelectionMapToChoices(rootStore);
  const addProductsParams = [{productId, quantity, preOrderRequested, options: {customTextFields, choices}}];
  const errorHandler = getAppErrorHandler(rootStore);
  const onAddToCartCompletedHandler = onAddToCartCompleted(rootStore, preOrderRequested);
  const publicApi = rootStore.isExperimentEnabled(SPECS.AddToCartUseStoresViewerScript)
    ? rootStore.storesPublicApi
    : rootStore.ecomPublicApi;

  if (rootStore.isExperimentEnabled(SPECS.PreloadCarts)) {
    const selectedAction = getSelectedAction(rootStore, preOrderRequested);

    return Promise.all([
      publicApi!.cart.addProducts(addProductsParams),
      preloadCartByAction(rootStore.ecomPublicApi, selectedAction),
    ])
      .then(() => onAddToCartCompletedHandler())
      .catch(errorHandler)
      .finally(action(() => (rootStore.$state.isAddToCartInProgress = false)));
  }

  return publicApi!.cart
    .addProducts(addProductsParams)
    .then(onAddToCartCompletedHandler)
    .catch(errorHandler)
    .finally(action(() => (rootStore.$state.isAddToCartInProgress = false)));
};

export const handleAddToCartClick = (rootStore: RootStore): WidgetEventHandler<any> => {
  return getCtaButtonClickHandler(rootStore, async () => {
    const addToCartAction = () =>
      rootStore.reportFedopsInteraction(FedopsInteractions.AddToCart, () => handleAddToCart(rootStore));

    return interruptAction(rootStore.wixCodeService.onAddToCartInterruption, addToCartAction);
  });
};
